// the keys of this are used in urls and for type-checking, the values for display purposes
export const MemberEntityType = {
  solicitor: 'Solicitor',
  barrister: 'Barrister',
  lawfirm: 'Law Firm',
  chamber: 'Chamber',
  lawyer: 'Lawyer',
}

export type MemberEntityTypeKey = keyof typeof MemberEntityType

export const VALID_PEOPLE_ENTITIES: ReadonlyArray<MemberEntityTypeKey> = [
  'barrister',
  'solicitor',
]

export const VALID_COMPANY_ENTITIES: ReadonlyArray<MemberEntityTypeKey> = [
  'lawfirm',
  'chamber',
]

export type MembershipType = 'NONE' | 'STANDARD' | 'PREMIUM' | 'COMMISSION'

export const PAID_MEMBERSHIP_TYPES: ReadonlyArray<MembershipType> = [
  'PREMIUM',
  'STANDARD',
  'COMMISSION',
]

// Note: a membership type might also be a null value, which obviously is also FREE
export const FREE_MEMBERSHIP_TYPES: ReadonlyArray<MembershipType> = ['NONE']

export const isPaidMembership = (membershipType: MembershipType) =>
  PAID_MEMBERSHIP_TYPES.includes(membershipType)

export const isFreeMembership = (membershipType: MembershipType | null) =>
  membershipType == null || FREE_MEMBERSHIP_TYPES.includes(membershipType)

export type MemberProfileShortType = {
  id: string
  friendly_id: string
  first_name?: string
  last_name?: string
  practice_name?: string // only appears for lawfirms and chambers
  bio?: string
  areas_of_law?: string[]
  hourly_rate?: number
  membership_type?: MembershipType
  avatar_url?: string
  email_address?: string
  phone_number?: string
  years_of_experience?: number
  is_favourite?: boolean
  profile_type?: MemberEntityTypeKey
}

export type MemberProfileExtendedType = {
  displayName: string
  memberEntity: MemberEntityTypeKey
  core: {
    first_name?: string
    last_name?: string
    phone_number?: string
    email_address: string
    bio: string
    avatar_url: string
    years_of_experience?: number
    is_verified?: boolean
    hourly_rate: number
    linkedin_profile_url: string
    chamber_name?: string
    organisation_name?: string
    practice_name?: string
    practice_name_initials?: string
    is_favourite?: boolean
  }
  professional_core: {
    accreditations?: string[]
    academic_qualifications?: string[]
    areas_of_law: string[]
    languages: string[]
    legal_aid_status: boolean
    legal_services: string[]
    memberships?: string[]
    practices_probate?: boolean
  }
  professional_details: {
    authorisation_date?: string
    authorisation_renewal_date?: string
    authorisation_valid_from?: string
    bsb_entity_id?: string
    bsb_number?: number
    bsb_authorisation?: string
    business_type?: string
    company_registration_number?: string
    company_number?: string
    company_structure?: string
    date_of_call?: string
    honours?: string[]
    insurance_certificate?: boolean
    member_status?: string
    office_location?: string
    primary_status?: string
    public_access?: boolean
    regional_cost?: number
    registered_office_address?: string
    sra_authorisation?: boolean
    sra_id?: number
    sra_number?: number
    type_of_body?: string
  }
  team?: {
    account_manager: {
      avatar?: string
      bio?: string
      email?: string
      name?: string
      linkedin_profile_url?: string
    }
    is_verified: boolean
    members: {
      avatar?: string
      bio?: string
      email?: string
      name?: string
    }[]
  }
  reviews_summary?: {
    overrall_rating: any
    num_of_reviews: number
    num_of_meetings: number
    joining_date: any
    review_totals: { legal_utopia: number; google: number; trust_pilot: number }
    num_of_stars: {
      '1_star': number
      '2_star': number
      '3_star': number
      '4_star': number
      '5_star': number
    }
  }
  metadata: {
    id: string
    distance: number
    membership_type: MembershipType
  }
}

export class MemberProfileExtendedClass implements MemberProfileExtendedType {
  displayName: string
  core: MemberProfileExtendedType['core']
  professional_core: MemberProfileExtendedType['professional_core']
  professional_details: MemberProfileExtendedType['professional_details']
  metadata: MemberProfileExtendedType['metadata']
  team?: MemberProfileExtendedType['team']
  reviews_summary?: MemberProfileExtendedType['reviews_summary']
  memberEntity: MemberEntityTypeKey
  initials: string
  constructor(
    profile: MemberProfileExtendedType,
    memberEntity: MemberEntityTypeKey,
  ) {
    this.core = profile.core
    this.metadata = profile.metadata
    this.memberEntity = memberEntity
    this.displayName = this.getDisplayName()
    this.initials = this.getInitials()
    this.metadata.distance = Math.round(this.metadata.distance)
    this.professional_core = profile.professional_core
    this.professional_details = profile.professional_details
    this.team = profile.team
    this.reviews_summary = profile.reviews_summary
  }

  private getDisplayName() {
    return this.core.last_name && this.core.first_name
      ? `${this.core.first_name} ${this.core.last_name}`
      : this.core.practice_name
        ? this.core.practice_name
        : this.core.chamber_name
          ? this.core.chamber_name
          : this.core.organisation_name || 'NA'
  }
  private getInitials() {
    if (!this.displayName) {
      return 'NA'
    }
    if (this.memberEntity === 'lawfirm') {
      return this.core.practice_name_initials
    }
    return this.displayName
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
  }
}

// Map of known keys to their display labels
// If key is unknown then we just render it as is
// TODO: possibly create a ProfessionalDetailsType and use it also
// in the MemberExtendedProfile type
export const professionalDetailsDisplayMap: Record<string, string> = {
  authorisation_date: 'Authorisation date',
  authorisation_renewal_date: 'Authorisation renewal date',
  authorisation_valid_from: 'Authorisation valid from',
  bsb_entity_id: 'BSB entity ID',
  bsb_number: 'BSB Number',
  bsb_authorisation: 'BSB Authorisation',
  business_type: 'Business type',
  company_registration_number: 'Company registration number',
  company_number: 'Company number',
  company_structure: 'Company structure',
  date_of_call: 'Date of call',
  honours: 'Honours',
  insurance_certificate: 'Insurance certificate',
  member_status: 'Member status',
  office_location: 'Office location',
  primary_status: 'Primary status',
  practices_probate: 'Practices probate',
  public_access: 'Public access',
  regional_cost: 'Regional cost',
  registered_office_address: 'Registered office address',
  sra_authorisation: 'SRA Authorisation',
  sra_id: 'SRA entity ID',
  sra_number: 'SRA Number',
  type_of_body: 'Type of body',
}
